import {
  ColumnConfigWithLabel,
  MappedPageConfig,
  PageConfig,
} from '../../../../api/customFields/pageConfig.interface';

export type ComponentsColumnId =
  | 'name'
  | 'fullyQualifiedName'
  | 'packageUrl'
  | 'version'
  | 'derivedEcosystem'
  | 'countVulnerabilities.total'
  | 'licensesData.fullName'
  | 'supplier.name'
  | 'description'
  | 'dateModified'
  | 'lastReleaseDate'
  | 'levelOfSupport'
  | 'endOfSupport';

// Column ID aliases to handle legacy column IDs
const COLUMN_ID_ALIASES: Record<string, ComponentsColumnId> = {
  longName: 'fullyQualifiedName',
  componentIdentifier: 'packageUrl',
};

export const getDefaultComponentsColumnsConfig = (
  t: Function,
): ColumnConfigWithLabel<ComponentsColumnId>[] => [
  {
    id: 'name',
    label: t('tables.components.headers.name'),
    visible: true,
    locked: true,
  },
  {
    id: 'fullyQualifiedName',
    label: t('tables.components.headers.longName'),
    visible: false,
    locked: false,
  },
  {
    id: 'packageUrl',
    label: t('tables.components.headers.componentIdentifier'),
    visible: true,
    locked: false,
  },
  {
    id: 'version',
    label: t('tables.components.headers.version'),
    visible: true,
    locked: false,
  },
  {
    id: 'derivedEcosystem',
    label: t('tables.components.headers.ecosystem'),
    visible: true,
    locked: false,
  },
  {
    id: 'countVulnerabilities.total',
    label: t('tables.components.headers.vulns'),
    visible: true,
    locked: false,
  },
  {
    id: 'licensesData.fullName',
    label: t('tables.components.headers.license'),
    visible: true,
    locked: false,
  },
  {
    id: 'supplier.name',
    label: t('tables.components.headers.source'),
    visible: true,
    locked: false,
  },
  {
    id: 'description',
    label: t('tables.components.headers.description'),
    visible: true,
    locked: false,
  },
  {
    id: 'dateModified',
    label: t('tables.components.headers.lastUpdated'),
    visible: true,
    locked: false,
  },
  {
    id: 'lastReleaseDate',
    label: t('tables.components.headers.lastReleaseDate'),
    visible: false,
    locked: false,
  },
  {
    id: 'levelOfSupport',
    label: t('tables.components.headers.levelOfSupport'),
    visible: false,
    locked: false,
  },
  {
    id: 'endOfSupport',
    label: t('tables.components.headers.endOfSupport'),
    visible: false,
    locked: false,
  },
];

export const mapComponentsPageConfig = ({
  data,
  t,
}: {
  data?: PageConfig<ComponentsColumnId>;
  t: Function;
}): MappedPageConfig<ComponentsColumnId> => {
  if (!data) {
    return { columnsConfig: getDefaultComponentsColumnsConfig(t) };
  }

  // Create a map of default configuration values to use as source of truth
  const defaultConfigMap = getDefaultComponentsColumnsConfig(t).reduce((map, config) => {
    map.set(config.id, config);
    return map;
  }, new Map<ComponentsColumnId, ColumnConfigWithLabel<ComponentsColumnId>>());

  // Map and alias legacy IDs, then dedupe by column id, preserving last occurrence
  const mappedConfigs = data.columnsConfig.map((columnConfig) => {
    const actualColumnId =
      (COLUMN_ID_ALIASES[columnConfig.id] as string) || columnConfig.id;
    const defaultColumnConfig = defaultConfigMap.get(
      actualColumnId as ComponentsColumnId,
    );
    if (defaultColumnConfig) {
      return {
        label: defaultColumnConfig.label || actualColumnId,
        id: actualColumnId as ComponentsColumnId,
        visible: columnConfig.visible,
        locked: defaultColumnConfig.locked,
      };
    }
    return {
      label: actualColumnId,
      id: actualColumnId as ComponentsColumnId,
      visible: columnConfig.visible,
      locked: columnConfig.locked,
    };
  });
  const uniqueConfigs = Array.from(
    new Map<string, ColumnConfigWithLabel<ComponentsColumnId>>(
      mappedConfigs.map((cfg) => [cfg.id, cfg]),
    ).values(),
  );
  return { columnsConfig: uniqueConfigs };
};
